import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as yup from 'yup';

import Form, { ErrorLabel } from 'partial/form/Form';
import Button from 'partial/components/Button';
import FormInputNumber from 'partial/form/FormInputNumber';
import { useCreateModal, ModalCard } from 'partial/components/Modal';
import { toastSuccess } from 'react-geek-toast';
import IconRequest from 'assets/images/icons/icon-request.png';

import { useAddBillerSettlement, useBillerSettlementList } from '../hooks';

export const useOpenAddBillerSettlement = () => {
  const [isLoading, submit] = useAddBillerSettlement();
  const [, , , fetchData] = useBillerSettlementList();
  const createModal = useCreateModal('ADD_BILLER_SETTLEMENT');
  const openModal = useCallback(() => {
    createModal({
      content: (onClose) => (
        <AddBillerSettlement
          onSubmit={submit}
          isLoading={isLoading}
          refetchData={fetchData}
          onClose={onClose}
        />
      ),
    });
  }, [createModal, submit, isLoading, fetchData]);
  return [openModal];
};

const validationSchema = yup.object().shape({
  amount_requested: yup.string().required('Required'),
});

function AddBillerSettlement({ onSubmit, isLoading, onClose, refetchData }) {
  const [step, setStep] = React.useState(1);
  const [validateOnChange, setValidateOnChange] = React.useState(false);
  const { setFieldValue, values, handleSubmit, errors } = useFormik({
    initialValues: {},
    validationSchema,
    onSubmit: (form) => {
      if (step === 1) {
        setStep(2);
        return;
      }
      onSubmit(form, () => {
        onClose();
        refetchData();
        toastSuccess('Settlement requested!');
      });
    },
    validateOnChange,
  });
  return (
    <ModalCard
      title={
        step === 1 ? 'Create Settlement Request' : 'Confirm Settlement Request'
      }
      size="sm"
      onClose={onClose}
    >
      <Form onSubmit={handleSubmit} error={errors} className="p-5">
        {step === 1 ? (
          <div>
            <FormInputNumber
              name="amount_requested"
              label="Amount"
              onSetFieldValue={setFieldValue}
              value={values?.amount_requested || ''}
              error={errors?.amount_requested}
            />
            <ErrorLabel name="amount_requested" />
          </div>
        ) : (
          <div className="flex items-center">
            <div className="mr-8">
              <h1 className="text-lg font-medium text-gray-500 mb-6">
                Are you sure you want to{' '}
                <span className="text-gray-800">add a new request</span>?
              </h1>
              <p className="text-gray-500 font-light">
                If you continue, this will create a new settlement request.
              </p>
            </div>
            <img alt="" src={IconRequest} />
          </div>
        )}
        <div className="flex items-center justify-center mt-4">
          <Button type="button" onClick={onClose} className="w-56">
            Cancel
          </Button>
          <Button
            primary
            type="submit"
            className="w-56"
            isLoading={isLoading}
            onClick={() => setValidateOnChange(true)}
          >
            Confirm
          </Button>
        </div>
      </Form>
    </ModalCard>
  );
}

AddBillerSettlement.propTypes = {
  refetchData: PropTypes.instanceOf(Function).isRequired,
  onSubmit: PropTypes.instanceOf(Function).isRequired,
  onClose: PropTypes.instanceOf(Function).isRequired,
  isLoading: PropTypes.bool,
};

AddBillerSettlement.defaultProps = {
  isLoading: false,
};

export default React.memo(AddBillerSettlement);
